import { useContext } from 'react';
import jwtDecode from 'jwt-decode';
import { AuthContext } from '../contexts/JWTContext';
import { storage, STORAGE_KEYS } from '../utils/storage';

// Define user roles enum
export enum UserRole {
  ADMIN = 'Admin',
  DISTRIBUTOR = 'Distributor',
  RETAILER = 'Retailer',
  STAFF = 'Staff'
}

// JWT payload type
interface JWTPayload {
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
  exp: number;
  iat: number;
  jti: string;
}

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('Auth context must be use inside AuthProvider');

  // Get user role from JWT token
  const getUserRole = (): UserRole | null => {
    try {
      // If not initialized, return null
      if (!context.isInitialized) {
        return null;
      }

      // If not authenticated, return null
      if (!context.isAuthenticated) {
        return null;
      }

      // Use sync method for immediate access, fallback to localStorage if needed
      const accessToken = storage.getItemSync<string>(STORAGE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        return null;
      }

      const decoded = jwtDecode<JWTPayload>(accessToken);
      const role = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

      // Map string role to enum
      switch (role) {
        case 'Admin':
          return UserRole.ADMIN;
        case 'Distributor':
          return UserRole.DISTRIBUTOR;
        case 'Retailer':
          return UserRole.RETAILER;
        case 'Staff':
          return UserRole.STAFF;
        default:
          return null;
      }
    } catch (error) {
      console.error('Error decoding token for role:', error);
      return null;
    }
  };

  // Check if user has required role
  const hasRole = (requiredRoles: UserRole[]): boolean => {
    const userRole = getUserRole();
    if (!userRole) return false;
    return requiredRoles.includes(userRole);
  };

  // Check if user can access specific feature
  const canAccess = (feature: string): boolean => {
    const userRole = getUserRole();
    if (!userRole) return false;

    switch (userRole) {
      case UserRole.ADMIN:
        return true; // Admin can access everything

      case UserRole.DISTRIBUTOR:
        // Distributor can access all except products
        return !feature.startsWith('products');

      case UserRole.RETAILER:
        // Retailer can only access orders
        return feature.startsWith('orders') || (feature.startsWith('users'));

      case UserRole.STAFF:
        // Staff can access limited order pages
        return feature.startsWith('orders') && !feature.includes('all');

      default:
        return false;
    }
  };

  return {
    ...context,
    getUserRole,
    hasRole,
    canAccess,
    userRole: getUserRole()
  };
};

export default useAuth;